import React from 'react';
import { Shield, FileText, Image, Cookie, MessageSquare } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import usePageSeo from '../hooks/usePageSeo';

const PrivacyPolicy = () => {
  usePageSeo({
    title: "Privacy Policy | ATL Regen Atlanta",
    description: "ATL Regen's privacy policy. Learn how we collect, use, and protect your personal information.",
    canonical: "https://www.atlregen.com/privacy-policy",
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
                <Shield className="h-5 w-5 text-[#20B2AA]" />
                <span className="text-sm font-medium">Your Privacy Matters</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
              <p className="text-xl text-gray-300">
                Learn how we collect, use, and protect your personal information.
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Personal Data Section */}
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="personal-data-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#20B2AA]/10 p-3 rounded-xl">
                    <FileText className="h-6 w-6 text-[#20B2AA]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1B1B3A]">What personal data we collect and why we collect it</h2>
                </div>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the aforementioned categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    When visitors leave comments on the site, we collect the data shown in the comments form, and also the visitor's IP address and browser user agent string to help with spam detection.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    An anonymized string created from your email address (also called a hash) may be provided to the Gravatar service to see if you are using it. The Gravatar service privacy policy is available here:{' '}
                    <a 
                      href="https://automattic.com/privacy/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#20B2AA] hover:text-[#1B8B85] underline"
                    >
                      https://automattic.com/privacy/
                    </a>
                    . After approval of your comment, your profile picture is visible to the public in the context of your comment.
                  </p>
                </div>
              </div>

              {/* Media Section */}
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="media-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#E63946]/10 p-3 rounded-xl">
                    <Image className="h-6 w-6 text-[#E63946]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1B1B3A]">Media</h2>
                </div>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 leading-relaxed">
                    If you upload images to the website, you should avoid uploading images with embedded location data (EXIF GPS) included. Visitors to the website can download and extract any location data from images on the website.
                  </p>
                </div>
              </div>

              {/* Contact Forms Section */}
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="contact-forms-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#1B1B3A]/10 p-3 rounded-xl">
                    <MessageSquare className="h-6 w-6 text-[#1B1B3A]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1B1B3A]">Contact forms</h2>
                </div>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 leading-relaxed">
                    Information submitted through contact forms is collected for the purpose of responding to your inquiries and providing requested services.
                  </p>
                </div>
              </div>

              {/* Cookies Section */}
              <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="cookies-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#20B2AA]/10 p-3 rounded-xl">
                    <Cookie className="h-6 w-6 text-[#20B2AA]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1B1B3A]">Cookies</h2>
                </div>
                <div className="prose prose-gray max-w-none space-y-4">
                  <p className="text-gray-600 leading-relaxed">
                    If you leave a comment on our site you may opt-in to saving your name, email address and website in cookies. These are for your convenience so that you do not have to fill in your details again when you leave another comment. These cookies will last for one year.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    If you visit our login page, we will set a temporary cookie to determine if your browser accepts cookies. This cookie contains no personal data and is discarded when you close your browser.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    When you log in, we will also set up several cookies to save your login information and your screen display choices. Login cookies last for two days, and screen options cookies last for a year. If you select "Remember Me", your login will persist for two weeks. If you log out of your account, the login cookies will be removed.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    If you edit or publish an article, an additional cookie will be saved in your browser. This cookie includes no personal data and simply indicates the post ID of the article you just edited. It expires after 1 day.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
